import MainBlock from "components/DesignSystem/LayoutBlocks/MainBlock";
import React, {
    useEffect,
    useContext,
    Fragment,
    useRef,
    useReducer,
} from "react";
import { useAuth } from "hooks/Auth";
import { useParams } from "react-router-dom";
import { useLocation } from "react-router-dom";
import { modal } from "components/HeadlessModal";
import {
    apiDel,
    apiGet,
    apiPost,
    apiPut,
    messageFromException,
} from "helpers/Request";
import {
    transform as channelTransformer,
    ChannelObjectSchema,
} from "schemas/Channel";
import { useHistory } from "react-router-dom";
import { toast } from "react-hot-toast";
import { AppContext } from "context/App";
import { Formik, Form } from "formik";
import TextInput from "components/FormElements/TextInput";
import Toolbar, { ToolbarGroup } from "components/Toolbar/Toolbar";
import ToolbarButton from "components/Toolbar/ToolbarButton";
import { SaveIcon, TrashIcon, SupportIcon } from "@heroicons/react/outline";
import { useContextualHelp } from "hooks/Help";
import { MetaPropertyField } from "components/MetaProperties/MetaProperty";
import CheckBox from "components/FormElements/CheckBox";
import PromptIfDirty from "components/FormElements/PromptIfDirty";
import DangerButton from "components/Buttons/DangerButton";
import SecondaryButton from "components/Buttons/SecondaryButton";
import RichEditor from "components/FormElements/RichEditor";

const reducer = (oldState, newState) => {
    return Object.assign({}, oldState, newState);
};

export default function ChannelEditScreen(props) {
    const { uuid } = useParams();
    const formRef = useRef();
    const location = useLocation();
    const auth = useAuth();
    const history = useHistory();
    const contextualHelp = useContextualHelp();

    const [state, setState] = useReducer(reducer, {
        channel: ChannelObjectSchema,
        formValues: ChannelObjectSchema,
        loading: false,
        saving: false,
    });
    const { setCurrentNav } = useContext(AppContext);

    const onSubmit = (values) => {
        const data = Object.assign({}, state.channel, values, {
            channelMeta: values.meta.map((meta) => ({
                uuid: meta.uuid || null,
                label: meta.label,
                type: meta.type,
                props: meta.props || null,
            })),
            meta: undefined,
        });

        setState({ saving: true });

        const apiAction = state.channel.uuid ? apiPut : apiPost;
        apiAction(
            "/v4/channel/" + (state.channel.uuid ? state.channel.uuid : ""),
            auth.publication,
            data
        )
            .then((resp) => {
                const item = channelTransformer(resp);
                toast.success("Channel saved");
                if (!state.channel.uuid) {
                    // was a new item, so redirect
                    history.push("/app/custom-content/channel/" + item.uuid);
                }
                setState({ channel: item, saving: false, loading: false });
            })
            .catch((err) => {
                setState({ saving: false });
                toast.error("Unable to save your changes, please try again.");
            });
    };

    const onDelete = () => {
        modal.warning({
            title: "Delete Channel",
            children: (
                <div>
                    <div className="mb-4">
                        <strong>
                            Are you sure you want to delete this channel?
                        </strong>
                    </div>
                    <div>
                        <strong>Note:</strong> This action cannot be undone.
                    </div>
                </div>
            ),
            buttons: () => (
                <Fragment>
                    <DangerButton onClick={deleteChannel}>Delete</DangerButton>
                    <SecondaryButton onClick={modal.dismiss}>
                        Cancel
                    </SecondaryButton>
                </Fragment>
            ),
        });
    };

    const deleteChannel = async () => {
        const id = toast.loading("Deleting channel...");
        return apiDel(`/v4/channel/${state.channel.uuid}`, auth.publication)
            .then((resp) => {
                toast.success("Channel deleted", { id });
                modal.dismiss();
                history.push("/app/custom-content/channel");
            })
            .catch((err) => {
                modal.dismiss();
                toast.dismiss(id);
                toast.error({
                    title: "Unable to delete channel",
                    children: messageFromException(err),
                });
            });
    };

    useEffect(() => {
        setState({ loading: true, channel: ChannelObjectSchema });

        if (!uuid || uuid === "new") {
            setTimeout(() => {
                setState({ loading: false });
            }, 0);
            return;
        }

        apiGet("/v4/channel/" + uuid, auth.publication)
            .then((response) => {
                const item = channelTransformer(response);

                setState({ channel: item, loading: false });
            })
            .catch((err) => {
                console.error(err);
                setState({ loading: false });
                modal.error({
                    title: "Unable to load channel",
                    children: messageFromException(err),
                });
            });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [location, uuid, auth.publication]);

    useEffect(() => {
        setCurrentNav("custom-content");
    }, [setCurrentNav]);

    useEffect(() => {
        // fixes a bug where the console notes a component updating another, unmounted
        // component. just delay the update until the next tick
        setTimeout(() => {
            setState({ formValues: Object.assign({}, state.channel) });
        });
    }, [state.channel]);

    // simply watches the loading indicator and resets the form when it changes.
    // this should always be the last `useEffect` in the sequence.
    useEffect(() => {
        setTimeout(() => {
            formRef.current.resetForm({
                values: state.formValues,
            });
        });
    }, [state.loading, state.formValues]);

    return (
        <MainBlock className="z-0">
            <Formik
                innerRef={formRef}
                initialValues={state.formValues}
                onSubmit={onSubmit}
                enableReinitialize={false}
            >
                {({ setFieldValue }) => (
                    <Form>
                        <PromptIfDirty />
                        <div className="mx-auto max-w-2xl space-y-4">
                            <Toolbar>
                                <ToolbarGroup>
                                    <ToolbarButton
                                        icon={<SaveIcon />}
                                        type="submit"
                                        disabled={state.saving}
                                        blocking={state.saving}
                                    >
                                        Save
                                    </ToolbarButton>
                                    <ToolbarButton
                                        icon={<TrashIcon />}
                                        onClick={onDelete}
                                        disabled={
                                            state.saving || !state.channel.uuid
                                        }
                                    >
                                        Delete
                                    </ToolbarButton>
                                </ToolbarGroup>
                                <ToolbarGroup className="ml-auto">
                                    <ToolbarButton
                                        icon={<SupportIcon />}
                                        className="text-green-600"
                                        onClick={() => {
                                            contextualHelp.showContextualHelp(
                                                "/custom-content/channel.html"
                                            );
                                        }}
                                    >
                                        Help
                                    </ToolbarButton>
                                </ToolbarGroup>
                            </Toolbar>
                            {state.loading ? (
                                <Fragment>
                                    <div className="h-10 w-full animate-pulse bg-gray-200"></div>
                                    <div className="h-10 w-full animate-pulse bg-gray-200"></div>
                                    <div className="h-10 w-full animate-pulse bg-gray-200"></div>
                                </Fragment>
                            ) : (
                                <div className="space-y-4 px-2 sm:px-0">
                                    <TextInput
                                        label="Title"
                                        name="title"
                                        disabled={state.saving}
                                    />
                                    <TextInput
                                        label="Slug"
                                        name="slug"
                                        disabled={state.saving}
                                        onChange={(e) => {
                                            const newVal = e.target.value
                                                .toLowerCase()
                                                .replace(/ {2,}/g, " ")
                                                .replace(/ /g, "-")
                                                .replace(/[^a-z0-9-]/g, "");

                                            setFieldValue("slug", newVal);
                                        }}
                                    />

                                    <RichEditor
                                        label="Description"
                                        name="description"
                                        disabled={state.saving}
                                    />

                                    <hr className="my-6" />

                                    <CheckBox
                                        name="hidden"
                                        label="Hidden"
                                        disabled={state.saving}
                                        helpertext="Hide this channel from users."
                                    />
                                    <CheckBox
                                        name="is_routable"
                                        label="Available as a route"
                                        disabled={state.saving}
                                        helpertext="Allow this channel to be a custom route."
                                    />
                                    <CheckBox
                                        name="settings_panel"
                                        label="Settings Panel"
                                        disabled={state.saving}
                                        helpertext="Make this channel appear as a settings panel."
                                    />

                                    <hr className="my-6" />
                                    <MetaPropertyField />
                                </div>
                            )}
                        </div>
                    </Form>
                )}
            </Formik>
        </MainBlock>
    );
}
